import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PageHeader from '../components/PageHeader';
import FormSection from '../components/FormSection';
import LoadingFallback from '../components/LoadingFallback';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { ApiKey, CreatedApiKey, Org } from '../api/types';

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export default function SettingsPage() {
  const qc = useQueryClient();
  const { toast, confirm } = useFeedback();
  const orgQuery = useQuery<Org>({
    queryKey: ['org', 'me'],
    queryFn: async () => (await api.get('/orgs/me')).data,
  });
  const apiKeysQuery = useQuery<ApiKey[]>({
    queryKey: ['api-keys'],
    queryFn: async () => (await api.get('/api-keys')).data,
  });
  const [name, setName] = useState('');
  const [type, setType] = useState('school');
  const [tz, setTz] = useState('UTC');
  const [keyName, setKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);

  useEffect(() => {
    if (orgQuery.data) {
      setName(orgQuery.data.name);
      setType(orgQuery.data.type);
      setTz(orgQuery.data.timezone || 'UTC');
    }
  }, [orgQuery.data]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch('/orgs/me', { name, type, timezone: tz })).data,
    onSuccess: () => {
      toast({ message: 'Organization updated', severity: 'success' });
      qc.invalidateQueries({ queryKey: ['org', 'me'] });
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  const createKey = useMutation({
    mutationFn: async () => (await api.post('/api-keys', { name: keyName })).data as CreatedApiKey,
    onSuccess: (data) => {
      setCreatedKey(data);
      setKeyName('');
      setKeyError(null);
      toast({ message: 'API key created', severity: 'success' });
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (e) => setKeyError(apiErrorMessage(e)),
  });

  const revokeKey = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/api-keys/${id}`)).data,
    onSuccess: () => {
      toast({ message: 'API key revoked', severity: 'success' });
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (e) => setKeyError(apiErrorMessage(e)),
  });

  const handleRevoke = async (apiKey: ApiKey) => {
    const ok = await confirm({
      title: 'Revoke API key?',
      message: `Revoke "${apiKey.name}"? Developers using this key will immediately lose access.`,
      confirmLabel: 'Revoke key',
      destructive: true,
    });
    if (ok) revokeKey.mutate(apiKey.id);
  };

  const copyCreatedKey = async () => {
    if (!createdKey?.key) return;
    await navigator.clipboard.writeText(createdKey.key);
    toast({ message: 'API key copied', severity: 'success' });
  };

  if (orgQuery.isLoading || !orgQuery.data) return <LoadingFallback />;

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Configure your organization." />
      <Card>
        <CardContent>
          <FormSection title="Organization">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                save.mutate();
              }}
              aria-label="Organization settings"
            >
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField label="Type" select value={type} onChange={(e) => setType(e.target.value)}>
                      <MenuItem value="school">School</MenuItem>
                      <MenuItem value="club">Club</MenuItem>
                      <MenuItem value="district">District</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField label="Timezone" select value={tz} onChange={(e) => setTz(e.target.value)}>
                      {TIMEZONES.map((t) => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Stack direction="row" justifyContent="flex-end">
                  <Button type="submit" variant="contained" disabled={save.isPending} aria-label="Save settings">
                    {save.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </FormSection>
          <FormSection
            title="Organization API Keys"
            description="Create shared API keys for this organization. Any organization admin can see or revoke these keys; they are not personal account keys."
            action={
              <Button
                component="a"
                href="/api/docs"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                aria-label="Open API documentation"
              >
                API Docs
              </Button>
            }
          >
            <Stack spacing={3}>
              {keyError && <Alert severity="error">{keyError}</Alert>}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  setKeyError(null);
                  createKey.mutate();
                }}
                aria-label="Create API key"
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="API key name"
                      value={keyName}
                      onChange={(e) => setKeyName(e.target.value)}
                      required
                      fullWidth
                      inputProps={{ 'aria-label': 'API key name' }}
                      helperText="Use a name that identifies the integration or developer."
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={createKey.isPending}
                      fullWidth
                      aria-label="Generate API key"
                    >
                      {createKey.isPending ? 'Generating…' : 'Generate key'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Stack spacing={1.5} aria-label="Organization API keys">
                {apiKeysQuery.isLoading && (
                  <Typography variant="body2" color="text.secondary">
                    Loading API keys...
                  </Typography>
                )}
                {!apiKeysQuery.isLoading && (apiKeysQuery.data?.length ?? 0) === 0 && (
                  <Alert severity="info">No API keys have been created for this organization.</Alert>
                )}
                {apiKeysQuery.data?.map((apiKey) => (
                  <Box
                    key={apiKey.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">{apiKey.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Prefix {apiKey.key_prefix} | Created {new Date(apiKey.created_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color={apiKey.is_active ? 'success.main' : 'text.secondary'}>
                        {apiKey.is_active
                          ? `Active${apiKey.last_used_at ? ` | Last used ${new Date(apiKey.last_used_at).toLocaleString()}` : ''}`
                          : `Revoked${apiKey.revoked_at ? ` | ${new Date(apiKey.revoked_at).toLocaleString()}` : ''}`}
                      </Typography>
                    </Box>
                    <Button
                      color="error"
                      variant="outlined"
                      disabled={!apiKey.is_active || revokeKey.isPending}
                      onClick={() => handleRevoke(apiKey)}
                      aria-label={`Revoke API key ${apiKey.name}`}
                    >
                      Revoke
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </FormSection>
        </CardContent>
      </Card>
      <Dialog
        open={Boolean(createdKey)}
        onClose={() => setCreatedKey(null)}
        aria-labelledby="created-api-key-title"
        aria-describedby="created-api-key-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="created-api-key-title">Copy API key</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">
              This key is shown once. Copy it now and store it securely.
            </Alert>
            <DialogContentText id="created-api-key-description">
              Use this value in the <code>X-API-Key</code> header when calling the public API.
            </DialogContentText>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.default',
              }}
            >
              <Typography
                component="code"
                sx={{ flex: 1, overflowWrap: 'anywhere', fontFamily: 'monospace', fontSize: 13 }}
              >
                {createdKey?.key}
              </Typography>
              <Tooltip title="Copy API key">
                <IconButton onClick={copyCreatedKey} aria-label="Copy generated API key">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedKey(null)} variant="contained" aria-label="Done copying API key">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
